import { Platform } from '@angular/cdk/platform'
import { DataSource } from '@angular/cdk/table'
import { AfterViewInit, ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { DateAdapter } from '@angular/material/core'
import { MatDialog } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'

import { AsiakasAsetuksetTyokieltoAsetaDialog } from '../../asiakkaat/asetukset/tyokielto-aseta.dialog'
import { AsiakasAsetuksetTyokieltoPaataDialog } from '../../asiakkaat/asetukset/tyokielto-paata.dialog'
import { LahetaMuistutusDialog, LahetaMuistutusDialogData } from './dialogit/laheta.dialog'

import { LoadingConfig } from 'app/_jaettu-angular/_components/loading.component'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'

import { LemonKuukausiDateAdapter } from '../../_jaettu-angular/_material/LemonKuukausiDateAdapter'
import { LaskuTransformingWrappingDataSource } from '../../_jaettu-angular/laskut/transforming-lasku.datasource'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { WindowSizeService } from '../../_jaettu-angular/service/window.service'

import { Lasku, LaskuBase, LaskunListaustietorivi, LaskunTila } from '@lemon/core-firebase/model/lasku'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { Timestamp } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'
import { AsiakkaanSopimuskausiService } from '../../_jaettu-lemonator/service/sopimuskausi.service'
import { LaskunLokalisoituTila } from '../../laskut/laskut.firestore.datasource'
import { MaksumuistutusService, TyokieltoListaRivi } from './maksumuistutus.datasource.service'

import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { Subject, firstValueFrom, merge } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'

export type LaskuProperties = 'nro' | 'pvm' | 'erapvm' | 'summa' | 'avoinna' | 'tila' | 'lataa' | 'asiakas' | 'kommentti' | 'ennakkolasku' | undefined

export interface SummienRullausData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
  edellinenLaskunTila: LaskunTila
}

export interface LahetysVirhe {
  error: Error
  laskunAvain: string
}

interface LaskuhakuLomake {
  nimiFirestore: string
}

@Component({
  selector: 'app-maksumuistutus',
  templateUrl: './maksumuistutus.component.html',
  styleUrls: ['./maksumuistutus.component.css'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    { provide: DateAdapter, useClass: LemonKuukausiDateAdapter, deps: [ErrorHandler, DateService, Platform, LemonTranslationService] },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaksumuistutusComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) _paginator: MatPaginator

  private _sortRef: MatSort
  @ViewChild(MatSort) set _sort(sort: MatSort) {
    this._sortRef = sort
    if (!sort) {
      return
    }
    Promise.resolve().then(() => {
      const target = this.aktiivinenValilehti === 0 ? this._tab0RawDataSource : this._tab1RawDataSource
      if (target.sort === sort) {
        return
      }
      sort.sort({
        disableClear: true,
        id: target.sort?.active ?? 'nro',
        start: target.sort?.direction === 'asc' ? 'asc' : 'desc',
      })
      target.sort = sort
    })
  }
  get _sort(): MatSort {
    return this._sortRef
  }

  @ViewChild('tab2Sort') set _tab2Sort(sort: MatSort) {
    if (!sort || this._tab2DataSource.sort === sort) {
      return
    }
    this._tab2DataSource.sort = sort
  }

  @ViewChild('tab3Sort') set _tab3Sort(sort: MatSort) {
    if (!sort || this._tab3DataSource.sort === sort) {
      return
    }
    this._tab3DataSource.sort = sort
  }

  @ViewChild('tab4Sort') set _tab4Sort(sort: MatSort) {
    if (!sort || this._tab4DataSource.sort === sort) {
      return
    }
    this._tab4DataSource.sort = sort
  }

  @ViewChild('tab5Sort') set _tab5Sort(sort: MatSort) {
    if (!sort || this._tab5DataSource.sort === sort) {
      return
    }
    this._tab5DataSource.sort = sort
  }

  private _ngUnsubscribe = new Subject<void>()
  private _kuuntelunKeskeytys = new Subject<void>()
  private _tyokieltoRivitLadattu = false
  private _tyokieltoStatusLadattu = false

  // Tab 0 ja 1: omat MatTableDataSource-instanssit + LaskuTransformingWrappingDataSource
  private _tab0RawDataSource = new MatTableDataSource<Lasku>()
  private _tab1RawDataSource = new MatTableDataSource<Lasku>()

  // Tab 2-5: työkielto-välilehtien MatTableDataSource-instanssit
  readonly _tab2DataSource = new MatTableDataSource<TyokieltoListaRivi>()
  readonly _tab3DataSource = new MatTableDataSource<TyokieltoListaRivi>()
  readonly _tab4DataSource = new MatTableDataSource<TyokieltoListaRivi>()
  readonly _tab5DataSource = new MatTableDataSource<TyokieltoListaRivi>()
  tab0DataSource: LaskuTransformingWrappingDataSource
  tab1DataSource: LaskuTransformingWrappingDataSource

  lomake: FieldTree<LaskuhakuLomake>
  private readonly _injector = inject(Injector)
  private readonly _changeDetectorRef = inject(ChangeDetectorRef)

  readonly propertiesToDisplay = signal<LaskuProperties[]>([])
  tilat: LaskunLokalisoituTila[] = []

  naytaHuomautus = false
  naytaKaikkiSummat = true
  readonly lahetysVirheet = signal<LahetysVirhe[]>([])
  aktiivinenValilehti = 0
  readonly lataa = signal(true)
  readonly lataaTyokielto = signal(true)

  readonly kaikkiTyokieltoRivit = signal<TyokieltoListaRivi[]>([])
  readonly tyokiellossaOlevatRivit = signal<TyokieltoListaRivi[]>([])
  readonly lopettaneetRivit = signal<TyokieltoListaRivi[]>([])
  readonly tyokieltoStatus = signal<Map<string, boolean>>(new Map())
  readonly tyokieltoAlkuMap = signal<Map<string, Timestamp | undefined>>(new Map())
  tyokiellossaOlevatColumnsToDisplay = ['asiakasNimi', 'laskujenMaara', 'tyokieltoAlku', 'toiminnot', 'piilota']
  tyokieltoColumnsToDisplay = ['asiakasNimi', 'laskujenMaara', 'toiminnot', 'piilota']
  purkuUhkaColumnsToDisplay = ['asiakasNimi', 'laskujenMaara', 'tyokielto', 'tyokieltoAlku', 'toiminnot', 'piilota']
  lopettaneetColumnsToDisplay = ['asiakasNimi', 'laskujenMaara', 'avoinSumma', 'lopetusPvm', 'lopettamisenSyy', 'tyokieltoAlku', 'toiminnot', 'piilota']

  loadingSettings: LoadingConfig = {
    backdropBackgroundColour: 'rgba(0,0,0,0.15)',
    backdropBorderRadius: '4px',
    fullScreenBackdrop: false,
    primaryColour: 'rgba(0, 0, 0, 0.2)',
    secondaryColour: 'rgba(0, 0, 0, 0.2)',
    tertiaryColour: 'rgba(0, 0, 0, 0.2)',
  }

  constructor(
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
    private _windowSizeService: WindowSizeService,
    private _translationService: LemonTranslationService,
    private _dateAdapter: DateAdapter<Date>,
    private _service: MaksumuistutusService,
    private _asiakasService: AsiakasService,
    private _timestampService: TimestampService,
    private _currencyService: CurrencyService,
    private _dateService: DateService,
    private _laskuSharedService: LaskuSharedService,
    private _lemonTranslationService: LemonTranslationService,
    private _sopimuskausiService: AsiakkaanSopimuskausiService,
  ) {
    this.tab0DataSource = new LaskuTransformingWrappingDataSource(this._tab0RawDataSource, this._currencyService, this._dateService, this._laskuSharedService, this._lemonTranslationService)
    this.tab0DataSource.naytaLisaViestit = false
    this.tab1DataSource = new LaskuTransformingWrappingDataSource(this._tab1RawDataSource, this._currencyService, this._dateService, this._laskuSharedService, this._lemonTranslationService)
    this.tab1DataSource.naytaLisaViestit = false

    this._service.asetaSortDataTaulukolle(this._tab0RawDataSource)
    this._service.asetaSortDataTaulukolle(this._tab1RawDataSource)

    this._tab2DataSource.sortingDataAccessor = (rivi, columnId) => {
      switch (columnId) {
        case 'asiakasNimi':
          return rivi.asiakasNimi?.toLowerCase() ?? ''
        case 'laskujenMaara':
          return rivi.laskujenMaara
        case 'tyokieltoAlku':
          return this.tyokieltoAlkuMap().get(rivi.asiakasAvain)?.seconds ?? 0
        default:
          return ''
      }
    }

    this._tab3DataSource.sortingDataAccessor = (rivi, columnId) => {
      switch (columnId) {
        case 'asiakasNimi':
          return rivi.asiakasNimi?.toLowerCase() ?? ''
        case 'laskujenMaara':
          return rivi.laskujenMaara
        default:
          return ''
      }
    }

    this._tab4DataSource.sortingDataAccessor = (rivi, columnId) => {
      switch (columnId) {
        case 'asiakasNimi':
          return rivi.asiakasNimi?.toLowerCase() ?? ''
        case 'laskujenMaara':
          return rivi.laskujenMaara
        case 'tyokielto':
          return this.tyokieltoStatus().get(rivi.asiakasAvain) ? 1 : 0
        case 'tyokieltoAlku':
          return this.tyokieltoAlkuMap().get(rivi.asiakasAvain)?.seconds ?? 0
        default:
          return ''
      }
    }

    this._tab5DataSource.sortingDataAccessor = (rivi, columnId) => {
      switch (columnId) {
        case 'asiakasNimi':
          return rivi.asiakasNimi?.toLowerCase() ?? ''
        case 'laskujenMaara':
          return rivi.laskujenMaara
        case 'avoinSumma':
          return rivi.avoinSumma ?? 0
        case 'lopetusPvm':
          return rivi.lopetusPvm ? this._dateService.localDateToNumber(rivi.lopetusPvm) : 0
        case 'lopettamisenSyy':
          return this.annaLopettamisenSyy(rivi).toLowerCase()
        case 'tyokieltoAlku':
          return rivi.tyokieltoAlku?.seconds ?? 0
        default:
          return ''
      }
    }
  }

  ngOnInit() {
    this.asetaListauksenKolumnit()

    this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kieli => {
      this._dateAdapter.setLocale(kieli)
    })

    this._service.laskunTilaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(
      tilat => {
        this.tilat = tilat
      },
      error => {
        this._errorHandler.handleError(error)
      },
    )

    this.lomake = form(signal<LaskuhakuLomake>({ nimiFirestore: this._aktiivinenRawDataSource.filter }), { injector: this._injector })

    arvonMuutokset(this.lomake.nimiFirestore, { injector: this._injector })
      .pipe(debounceTime(500))
      .subscribe((value: string) => {
        if (value) {
          const val = value.trim().toLowerCase()
          this._aktiivinenRawDataSource.filter = val
        } else {
          this._aktiivinenRawDataSource.filter = null
        }
      })

    this._kuunteleListoja()
  }

  private _kuunteleListoja() {
    const lopetus$ = merge(this._ngUnsubscribe, this._kuuntelunKeskeytys)

    // Tab 0
    this._service.ensimmainenMuistutus$.pipe(takeUntil(lopetus$)).subscribe(laskut => {
      this._tab0RawDataSource.data = laskut
      this.lataa.set(false)
      this._changeDetectorRef.markForCheck()
    })

    // Tab 1
    this._service.toinenMuistutus$.pipe(takeUntil(lopetus$)).subscribe(laskut => {
      this._tab1RawDataSource.data = laskut
      this._changeDetectorRef.markForCheck()
    })

    // Tabs 2-4
    this._service.tyokieltoKaikkiRivit$.pipe(takeUntil(lopetus$)).subscribe(rivit => {
      this.kaikkiTyokieltoRivit.set(rivit)
      this._tyokieltoRivitLadattu = true
      this._paivitaTyokieltoLatausStatus()
      this._refreshTyokieltoDataSources()
    })

    this._service.tyokieltoAsiakkaat$.pipe(takeUntil(lopetus$)).subscribe(rivit => {
      this.tyokiellossaOlevatRivit.set(rivit)
      this._tab2DataSource.data = rivit
    })

    // Tab 5
    this._service.lopettaneetTyokieltoAsiakkaat$.pipe(takeUntil(lopetus$)).subscribe(rivit => {
      this.lopettaneetRivit.set(rivit)
      this._tab5DataSource.data = rivit
    })

    // Työkielto-status reaktiivisesti (korvaa vanhan vuotavan nested-subscribe -kuvion)
    this._service.tyokieltoStatusMap$.pipe(takeUntil(lopetus$)).subscribe(statusMap => {
      this.tyokieltoStatus.set(new Map([...statusMap].map(([k, v]) => [k, v.tyokielto])))
      this.tyokieltoAlkuMap.set(new Map([...statusMap].map(([k, v]) => [k, v.tyokieltoAlku])))
      this._tyokieltoStatusLadattu = true
      this._paivitaTyokieltoLatausStatus()
      this._refreshTyokieltoDataSources()
    })
  }

  ngAfterViewInit() {
    this._windowSizeService.sizeObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(koko => {
      setTimeout(() => {
        this.asetaListauksenKolumnit()
      }, 250)
    })
  }

  private asetaListauksenKolumnit() {
    this.propertiesToDisplay.set(['tila', 'nro', 'asiakas', 'pvm', 'erapvm', 'summa', 'avoinna', 'lataa', 'ennakkolasku', 'kommentti'])
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  poistaListalta(rivi: LaskunListaustietorivi) {
    this._service.deleteInvoice(rivi.avain)
    // Suoraan data sourcesta poisto säilyttää nykyisen lajittelujärjestyksen
    this._aktiivinenRawDataSource.data = this._aktiivinenRawDataSource.data.filter(l => l.avain !== rivi.avain)
  }

  poistaKaikkiListalta(rivi: LaskunListaustietorivi) {
    this._service.deleteAllInvoices(rivi.avain, this._aktiivinenRawDataSource.data)
    this._aktiivinenRawDataSource.data = this._aktiivinenRawDataSource.data.filter(l => l.avain === rivi.avain)
  }

  piilotaTyokieltoListalta(rivi: TyokieltoListaRivi) {
    this._service.piilotaTyokieltoAsiakas(rivi.asiakasAvain)
    this.kaikkiTyokieltoRivit.set(this.kaikkiTyokieltoRivit().filter(r => r.asiakasAvain !== rivi.asiakasAvain))
    this._refreshTyokieltoDataSources()
  }

  piilotaKaikkiMuutTyokieltoListalta(rivi: TyokieltoListaRivi) {
    const kaikkiRivit = this.aktiivinenValilehti === 3 ? this.tyokieltoAsiakkaatIlmanTyokieltoa : this.purkuUhkaAsiakkaatFiltered
    this._service.piilotaKaikkiMuutTyokieltoAsiakkaat(rivi.asiakasAvain, kaikkiRivit)
    const toHide = new Set(kaikkiRivit.filter(r => r.asiakasAvain !== rivi.asiakasAvain).map(r => r.asiakasAvain))
    this.kaikkiTyokieltoRivit.set(this.kaikkiTyokieltoRivit().filter(r => !toHide.has(r.asiakasAvain)))
    this._refreshTyokieltoDataSources()
  }

  piilotaTyokiellossaOlevaListalta(rivi: TyokieltoListaRivi) {
    this._service.piilotaTyokiellossaOlevaAsiakas(rivi.asiakasAvain)
    this.tyokiellossaOlevatRivit.set(this.tyokiellossaOlevatRivit().filter(r => r.asiakasAvain !== rivi.asiakasAvain))
    this._tab2DataSource.data = this.tyokiellossaOlevatRivit()
  }

  piilotaKaikkiMuutTyokiellossaOlevatListalta(rivi: TyokieltoListaRivi) {
    this._service.piilotaKaikkiMuutTyokiellossaOlevatAsiakkaat(rivi.asiakasAvain, this.tyokiellossaOlevatRivit())
    this.tyokiellossaOlevatRivit.set(this.tyokiellossaOlevatRivit().filter(r => r.asiakasAvain === rivi.asiakasAvain))
    this._tab2DataSource.data = this.tyokiellossaOlevatRivit()
  }

  piilotaLopettanutListalta(rivi: TyokieltoListaRivi) {
    this._service.piilotaLopettanutAsiakas(rivi.asiakasAvain)
    this.lopettaneetRivit.set(this.lopettaneetRivit().filter(r => r.asiakasAvain !== rivi.asiakasAvain))
    this._tab5DataSource.data = this.lopettaneetRivit()
  }

  piilotaKaikkiMuutLopettaneetListalta(rivi: TyokieltoListaRivi) {
    this._service.piilotaKaikkiMuutLopettaneetAsiakkaat(rivi.asiakasAvain, this.lopettaneetRivit())
    this.lopettaneetRivit.set(this.lopettaneetRivit().filter(r => r.asiakasAvain === rivi.asiakasAvain))
    this._tab5DataSource.data = this.lopettaneetRivit()
  }

  annaLopettamisenSyy(rivi: TyokieltoListaRivi): string {
    if (!rivi.lopettamisenSyy) {
      return '-'
    }
    return this._sopimuskausiService.lokalisoidutSopimuksenLopettamissyyt.find(syy => syy.arvo === rivi.lopettamisenSyy)?.nimi ?? rivi.lopettamisenSyy
  }

  private _paivitaTyokieltoLatausStatus() {
    if (this._tyokieltoRivitLadattu && this._tyokieltoStatusLadattu) {
      this.lataaTyokielto.set(false)
    }
  }

  private _refreshTyokieltoDataSources() {
    this._tab3DataSource.data = this.tyokieltoAsiakkaatIlmanTyokieltoa
    this._tab4DataSource.data = this.purkuUhkaAsiakkaatFiltered
  }

  trackLaskuByAvain(index: number, item: LaskunListaustietorivi) {
    return item.avain
  }

  trackTyokieltoByAvain(index: number, item: TyokieltoListaRivi) {
    return item.asiakasAvain
  }

  lahetaMuistutuslaskut() {
    const dialogData: LahetaMuistutusDialogData = {
      laskut: this._aktiivinenRawDataSource.data,
      toinenMuistutus: this.aktiivinenValilehti === 1,
    }

    // Jokainen lähetetty muistutus muuttaa erääntyneiden laskujen listaa, ja kuuntelijat laskisivat kaikki välilehdet
    // ja avaisivat asiakaskohtaiset kuuntelijat uudelleen jokaisen laskun jälkeen, mikä kaataa selaimen isoilla erillä
    this._kuuntelunKeskeytys.next()
    const lahetaDialogRef = this._dialog.open<LahetaMuistutusDialog, LahetaMuistutusDialogData>(LahetaMuistutusDialog, { data: dialogData, panelClass: 'ilman-paddingia', disableClose: true })

    lahetaDialogRef
      .afterClosed()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(result => {
        if (result) {
          this.lahetysVirheet.set(result)
        }
        this.lataa.set(true)
        this._kuunteleListoja()
      })
  }

  private get _aktiivinenRawDataSource(): MatTableDataSource<Lasku> {
    return this.aktiivinenValilehti === 0 ? this._tab0RawDataSource : this._tab1RawDataSource
  }

  get aktiivinenDataSource(): DataSource<LaskunListaustietorivi> {
    return this.aktiivinenValilehti === 0 ? this.tab0DataSource : this.tab1DataSource
  }

  get laskujenMaara(): number {
    return this._aktiivinenRawDataSource.data.length
  }

  get matDataSource(): MatTableDataSource<Lasku> {
    return this._aktiivinenRawDataSource
  }

  get tyokieltoAsiakkaatIlmanTyokieltoa(): TyokieltoListaRivi[] {
    return this.kaikkiTyokieltoRivit().filter(rivi => !this.tyokieltoStatus().get(rivi.asiakasAvain) && rivi.laskujenMaara === 2)
  }

  get tyokieltoAsiakkaatTyokiellossa(): TyokieltoListaRivi[] {
    return this.tyokiellossaOlevatRivit()
  }

  get purkuUhkaAsiakkaatFiltered(): TyokieltoListaRivi[] {
    return this.kaikkiTyokieltoRivit().filter(rivi => rivi.laskujenMaara >= 3)
  }

  async merkitseMaksetuksi(rivi: LaskunListaustietorivi) {}

  vaihdaValilehti(indeksi: number) {
    this.aktiivinenValilehti = indeksi
  }

  async avaaTyokieltoDialogAsiakkaalle(rivi: TyokieltoListaRivi) {
    const onTyokiellossa = rivi.tyokielto ?? this.tyokieltoStatus().get(rivi.asiakasAvain) ?? false
    let tulos: boolean
    if (onTyokiellossa) {
      const dialogRef = this._dialog.open<AsiakasAsetuksetTyokieltoPaataDialog, undefined, boolean>(AsiakasAsetuksetTyokieltoPaataDialog)
      tulos = await firstValueFrom(dialogRef.afterClosed())
    } else {
      const dialogRef = this._dialog.open<AsiakasAsetuksetTyokieltoAsetaDialog, undefined, boolean>(AsiakasAsetuksetTyokieltoAsetaDialog)
      tulos = await firstValueFrom(dialogRef.afterClosed())
    }
    if (!tulos) {
      return
    }
    const asiakas = await firstValueFrom(this._asiakasService.annaAsiakasObservable(rivi.asiakasAvain))
    if (!asiakas) {
      return
    }
    const nykyinenTyokielto = asiakas.tyokielto
    const nykyinenAlku = asiakas.tyokieltoAlku
    asiakas.tyokielto = !nykyinenTyokielto
    asiakas.tyokieltoAlku = asiakas.tyokielto ? this._timestampService.now() : undefined
    try {
      await this._asiakasService.paivitaAsiakas(asiakas, 'tilikaudet-eivat-voineet-muuttua', true, false, true)
      const message = asiakas.tyokielto ? 'Työkielto asetettiin onnistuneesti.' : 'Työkielto poistettiin onnistuneesti.'
      this._snackbar.open(message, 'OK', { duration: 5000, verticalPosition: 'top' })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('TALLENNUS EPÄONNISTUI. Kerro terveiset ylläpidolle.', 'Pilalla', { duration: 10000, verticalPosition: 'top' })
      asiakas.tyokielto = nykyinenTyokielto
      asiakas.tyokieltoAlku = nykyinenAlku
    }
  }
}
